import { useState, useEffect, useCallback, useMemo } from "react";
import { TreeTable } from "primereact/treetable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { TabView, TabPanel } from "primereact/tabview";
import { UsuarioFormDialog } from "../components/dialogs/UsuarioFormDialog";
import { getUsuariosTabla } from "../../../services/backend/adminService";
import { ensureCatalogAuth } from "../../../services/backend/authService";

const Usuarios = () => {
    const [rows, setRows] = useState([]);
    const [rolesPorUsuario, setRolesPorUsuario] = useState({});
    const [jerarquiasPorUsuario, setJerarquiasPorUsuario] = useState({});
    const [expandedKeys, setExpandedKeys] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedUsuario, setSelectedUsuario] = useState(null);

    const [visibleAgregar, setVisibleAgregar] = useState(false);
    const [visibleEditar, setVisibleEditar] = useState(false);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            await ensureCatalogAuth();
            const response = await getUsuariosTabla(false);
            const usuarios = response.usuarios || [];
            setRows(usuarios);
            const mapaRoles = {};
            (response.usuario_rol || [])
                .filter((ur) => ur.activo)
                .forEach((ur) => {
                    if (!mapaRoles[ur.idUsuario]) mapaRoles[ur.idUsuario] = [];
                    mapaRoles[ur.idUsuario].push(ur.rol);
                });
            setRolesPorUsuario(mapaRoles);
            const mapaJerarquias = {};
            (response.usuario_jerarquia || []).forEach((j) => {
                if (!mapaJerarquias[j.idUsuario]) mapaJerarquias[j.idUsuario] = [];
                mapaJerarquias[j.idUsuario].push(j);
            });
            setJerarquiasPorUsuario(mapaJerarquias);
        } catch {
            setRows([]);
            setRolesPorUsuario({});
            setJerarquiasPorUsuario({});
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const activeNodes = useMemo(() => {
        return rows
            .filter((u) => u.activo === true)
            .map((u, i) => ({
                key: String(i),
                data: { ...u, _isUsuario: true },
                children: (jerarquiasPorUsuario[u.id] || []).map((j, jIdx) => ({
                    key: `${i}-${jIdx}`,
                    data: { ...j, _isJerarquia: true },
                })),
            }));
    }, [rows, jerarquiasPorUsuario]);

    const inactiveNodes = useMemo(() => {
        return rows
            .filter((u) => u.activo === false)
            .map((u, i) => ({
                key: String(i),
                data: { ...u, _isUsuario: true },
                children: (jerarquiasPorUsuario[u.id] || []).map((j, jIdx) => ({
                    key: `${i}-${jIdx}`,
                    data: { ...j, _isJerarquia: true },
                })),
            }));
    }, [rows, jerarquiasPorUsuario]);

    const header = (
        <div className="flex align-items-center justify-content-between">
            <span className="text-xl font-semibold">Usuarios</span>
            <Button icon="pi pi-plus" label="Agregar" onClick={() => setVisibleAgregar(true)} severity="success" />
        </div>
    );

    const actionTemplate = (node) => {
        if (node.data._isJerarquia) return null;
        return (
            <Button icon="pi pi-pencil" aria-label="Editar" tooltip="editar" outlined onClick={() => {
                setSelectedUsuario(node.data);
                setVisibleEditar(true);
            }} />
        );
    };

    const codigoTemplate = (node) => {
        if (node.data._isJerarquia) return null;
        return node.data.codigo;
    };

    const nombreTemplate = (node) => {
        if (node.data._isJerarquia) return null;
        return node.data.nombre;
    };

    const usuarioTemplate = (node) => {
        if (node.data._isJerarquia) return null;
        return node.data.usuario;
    };

    const emailTemplate = (node) => {
        if (node.data._isJerarquia) return null;
        return node.data.email;
    };

    const perfilTemplate = (node) => {
        if (node.data._isJerarquia) return null;
        return node.data.perfil?.descripcion || '';
    };

    const rolesTemplate = (node) => {
        if (node.data._isJerarquia) return null;
        const roles = rolesPorUsuario[node.data.id] || [];
        return roles.length > 0
            ? roles.map((r) => <Tag key={r} value={r} className="mr-1" />)
            : <span className="text-500">Sin rol</span>;
    };

    const notificacionesTemplate = (node) => {
        if (node.data._isJerarquia) return null;
        return node.data.recibeNotificaciones ? "Sí" : "No";
    };

    const responsableTemplate = (node) => {
        if (!node.data._isJerarquia) return null;
        return node.data.nombreResponsable || "—";
    };

    const limiteTemplate = (node) => {
        if (!node.data._isJerarquia) return null;
        return node.data.limiteCredito != null
            ? `$${Number(node.data.limiteCredito).toLocaleString("es-CL")}`
            : "—";
    };

    const fechaInicioTemplate = (node) => {
        if (!node.data._isJerarquia) return null;
        return node.data.fechaInicio + " a "+  node.data.fechaFin || "—";
    };

    const fechaRegistroTemplate = (node) => {
        if (!node.data._isJerarquia) return null;
        return node.data.fecha || "—";
    };

    return (
        <>
            <div className="py-3">
                <div className="grid">
                    <div className="col-12">
                        <h4 className="font-bold mb-1">Administración de Usuarios</h4>
                        <p className="text-700 text-lg line-height-3 mb-3">
                            Aqui puedes administrar tus usuarios.
                        </p>

                        <TabView>
                            <TabPanel header={`Activos (${activeNodes.length})`}>
                                <TreeTable
                                    key="active-table"
                                    value={activeNodes}
                                    header={header}
                                    showGridlines
                                    paginator
                                    rows={10}
                                    stripedRows
                                    scrollable
                                    scrollHeight="calc(99vh - 370px)"
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} usuarios"
                                    loading={loading}
                                    expandedKeys={expandedKeys}
                                    onToggle={(e) => setExpandedKeys(e.value)}
                                >
                                    <Column body={actionTemplate} header="Acciones" style={{ width: "8%" }} />
                                    {/* <Column field="codigo" header="Código" style={{ width: "15%" }} body={codigoTemplate} /> */}
                                    <Column header="Nombre" style={{ width: "20%" }} body={nombreTemplate} expander/>
                                    <Column header="Usuario" style={{ width: "20%" }} body={usuarioTemplate} />
                                    {/* <Column header="Email" style={{ width: "28%" }} body={emailTemplate} /> */}
                                    <Column header="Perfil" style={{ width: "20%" }} body={perfilTemplate} />
                                    <Column header="Roles" style={{ width: "10%" }} body={rolesTemplate} />
                                    {/* <Column header="Notif." style={{ width: "8%" }} body={notificacionesTemplate} /> */}
                                    <Column header="Responsable" style={{ width: "20%" }} body={responsableTemplate} />
                                    <Column header="Límite Crédito" style={{ width: "10%" }} body={limiteTemplate} />
                                    <Column header="Vigencia" style={{ width: "10%" }} body={fechaInicioTemplate} />
                                    {/* <Column header="Fecha registro" style={{ width: "14%" }} body={fechaRegistroTemplate} /> */}
                                </TreeTable>
                            </TabPanel>
                            <TabPanel header={`Inactivos (${inactiveNodes.length})`}>
                                <TreeTable
                                    key="inactive-table"
                                    value={inactiveNodes}
                                    header={header}
                                    showGridlines
                                    paginator
                                    rows={10}
                                    stripedRows
                                    scrollable
                                    scrollHeight="calc(99vh - 370px)"
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} usuarios"
                                    loading={loading}
                                    expandedKeys={expandedKeys}
                                    onToggle={(e) => setExpandedKeys(e.value)}
                                >
                                    <Column body={actionTemplate} header="Acciones" style={{ width: "8%" }} />
                                    {/* <Column field="codigo" header="Código" expander style={{ width: "15%" }} body={codigoTemplate} /> */}
                                    <Column header="Nombre" style={{ width: "20%" }} body={nombreTemplate} expander/>
                                    <Column header="Usuario" style={{ width: "20%" }} body={usuarioTemplate} />
                                    {/* <Column header="Email" style={{ width: "28%" }} body={emailTemplate} /> */}
                                    <Column header="Perfil" style={{ width: "20%" }} body={perfilTemplate} />
                                    <Column header="Roles" style={{ width: "20%" }} body={rolesTemplate} />
                                    {/* <Column header="Notif." style={{ width: "8%" }} body={notificacionesTemplate} /> */}
                                    <Column header="Responsable" style={{ width: "20%" }} body={responsableTemplate} />
                                    <Column header="Límite Crédito" style={{ width: "10%" }} body={limiteTemplate} />
                                    <Column header="Vigencia" style={{ width: "10%" }} body={fechaInicioTemplate} />
                                    {/* <Column header="Fecha registro" style={{ width: "14%" }} body={fechaRegistroTemplate} /> */}
                                </TreeTable>
                            </TabPanel>
                        </TabView>
                    </div>
                </div>
            </div>

            <UsuarioFormDialog visible={visibleAgregar} mode="agregar" onCancel={() => setVisibleAgregar(false)} onSave={cargar} />
            <UsuarioFormDialog visible={visibleEditar} mode="editar" usuario={selectedUsuario} onSave={cargar} onCancel={() => {
                setVisibleEditar(false);
                setSelectedUsuario(null);
            }} />
        </>
    );
};

export default Usuarios;
